import {
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Skeleton,
  Tag,
  TagCloseButton,
  TagLabel,
  Text,
  useColorModeValue,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { SearchIcon } from '@chakra-ui/icons';
import React from 'react';
import {
  useCreateSymptomMutation,
  useGetSymptomsForSelectionQuery,
} from 'api/symptomsSlice';

const SymptomPicker = ({ value = [], onChange }) => {
  const toast = useToast();
  const [query, setQuery] = React.useState('');
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.200');
  const menuBg = useColorModeValue('white', 'navy.800');
  const hoverBg = useColorModeValue('gray.50', 'whiteAlpha.100');
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const muted = useColorModeValue('gray.500', 'gray.400');

  const { data, isLoading } = useGetSymptomsForSelectionQuery(undefined, {
    refetchOnMountOrArgChange: true,
  });
  const [createSymptom, { isLoading: isCreating }] = useCreateSymptomMutation();

  const library = Array.isArray(data?.data) ? data.data : [];
  const selected = value.filter(Boolean);
  const selectedSet = new Set(selected.map((name) => name.toLowerCase()));

  const filtered = library.filter((item) => {
    if (selectedSet.has(String(item.name).toLowerCase())) return false;
    if (!query.trim()) return true;
    return String(item.name).toLowerCase().includes(query.trim().toLowerCase());
  });

  const addName = (name) => {
    const next = name.trim();
    if (!next) return;
    if (selectedSet.has(next.toLowerCase())) return;
    onChange([...selected, next]);
    setQuery('');
  };

  const addFromLibrary = (item) => addName(item.name);

  const addNew = async () => {
    const next = query.trim();
    if (!next) return;
    try {
      const response = await createSymptom({ name: next }).unwrap();
      const createdName = response?.data?.name || next;
      addName(createdName);
      toast({
        title: response?.message || 'Added to symptom library',
        status: 'success',
        duration: 2500,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Could not add symptom',
        description: error?.data?.message || 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const exactMatch = library.some(
    (item) => String(item.name).toLowerCase() === query.trim().toLowerCase()
  );

  return (
    <Box>
      <Text color={muted} fontSize="sm" mb={3}>
        Select symptoms from the library. New names are saved once so they stay consistent.
      </Text>

      <InputGroup mb={3}>
        <InputLeftElement>
          <SearchIcon color="gray.400" />
        </InputLeftElement>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search the symptom library..."
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (filtered[0]) addFromLibrary(filtered[0]);
              else addNew();
            }
          }}
        />
      </InputGroup>

      {query.trim() && !exactMatch && (
        <Button size="sm" mb={3} colorScheme="blue" variant="outline" isLoading={isCreating} onClick={addNew}>
          Add “{query.trim()}” to library
        </Button>
      )}

      {selected.length > 0 && (
        <Flex wrap="wrap" gap={2} mb={4}>
          {selected.map((name) => (
            <Tag key={name} size="lg" borderRadius="full" colorScheme="green">
              <TagLabel>{name}</TagLabel>
              <TagCloseButton onClick={() => onChange(selected.filter((item) => item !== name))} />
            </Tag>
          ))}
        </Flex>
      )}

      <Box
        maxH="260px"
        overflowY="auto"
        borderWidth="1px"
        borderColor={borderColor}
        borderRadius="lg"
        bg={menuBg}
      >
        {isLoading ? (
          <Box p={4}>
            {Array.from({ length: 6 }).map((_, index) => (
              <Skeleton key={index} h="28px" mb="10px" borderRadius="md" />
            ))}
          </Box>
        ) : filtered.length === 0 ? (
          <Text p={4} color={muted} fontSize="sm">
            {library.length === 0
              ? 'The library is empty. Type a symptom and add it.'
              : 'No matching symptoms. Add this name to the library.'}
          </Text>
        ) : (
          <VStack align="stretch" spacing={0}>
            {filtered.slice(0, 40).map((item) => (
              <Box
                key={item.id}
                px={4}
                py={3}
                cursor="pointer"
                _hover={{ bg: hoverBg }}
                onClick={() => addFromLibrary(item)}
              >
                <Text color={textColor} fontWeight="600">{item.name}</Text>
              </Box>
            ))}
          </VStack>
        )}
      </Box>
    </Box>
  );
};

export default SymptomPicker;
